import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Spinner, Textarea } from '@fluentui/react-components';
import {
    CheckmarkCircle20Filled,
    Star20Filled,
    Star20Regular,
    ThumbDislike20Filled,
    ThumbDislike20Regular,
    ThumbLike20Filled,
    ThumbLike20Regular,
} from '@fluentui/react-icons';

import { AppState } from '../../../redux/reducers';
import * as HelpDocumentationService from '../../../services/HelpDocumentation.Service';
import * as ComponentFunctions from './HelpDocumentationFeedback.Functions';

import './HelpDocumentationFeedback.Styles.css';

export interface HelpDocumentationFeedbackProps {
    /** Página sobre la que se opina. */
    linkCode: string;
}

/**
 * Opinión del lector sobre la página de ayuda que acaba de leer.
 *
 * Se pide en dos tiempos, y no en un formulario. Primero el pulgar, que se envía
 * solo: es la respuesta de un clic, y pedir más que eso es como estas funciones
 * terminan sin datos. Recién después —ya con la respuesta guardada— se ofrece el
 * detalle, que es opcional y para el que quiera darlo.
 *
 * Los dos envíos van al mismo endpoint a propósito: el servidor reemplaza la
 * respuesta previa del mismo lector en lugar de sumar una segunda, así que el
 * segundo envío completa al primero en vez de duplicarlo.
 */
const HelpDocumentationFeedbackComponent: React.FC<HelpDocumentationFeedbackProps> = ({ linkCode }) => {
    const [t] = useTranslation();
    const token = useSelector(
        (state: AppState) => (state as any)?.oidc?.user?.access_token as string | undefined
    );

    const [wasHelpful, setWasHelpful] = useState<boolean | undefined>(undefined);
    const [rating, setRating] = useState<number | undefined>(undefined);
    const [comment, setComment] = useState<string>('');

    const [isSending, setIsSending] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);
    const [isDone, setIsDone] = useState<boolean>(false);

    // Otra página es otra opinión: lo respondido acá no dice nada de la siguiente.
    useEffect(() => {
        setWasHelpful(undefined);
        setRating(undefined);
        setComment('');
        setIsSending(false);
        setFailed(false);
        setIsDone(false);
    }, [linkCode]);

    const send = async (feedback: ReturnType<typeof ComponentFunctions.buildFeedback>) => {
        if (!token || !ComponentFunctions.hasSomethingToSay(feedback)) return false;

        setIsSending(true);
        setFailed(false);

        try {
            await HelpDocumentationService.SubmitDocumentationFeedback(linkCode, feedback, token);
            return true;
        } catch {
            setFailed(true);
            return false;
        } finally {
            setIsSending(false);
        }
    };

    const onAnswer = async (helpful: boolean) => {
        // Se pinta el pulgar antes de que vuelva el servidor: la respuesta ya fue
        // dada, y dejar el botón sin reaccionar invita a un segundo clic.
        setWasHelpful(helpful);

        const saved = await send(ComponentFunctions.buildFeedback(helpful));
        if (!saved) setWasHelpful(undefined);
    };

    const onSendDetail = async () => {
        const saved = await send(ComponentFunctions.buildFeedback(wasHelpful, rating, comment));
        if (saved) setIsDone(true);
    };

    // Sin sesión no hay a quién atribuir la respuesta —el servidor la rechaza— así
    // que se calla en vez de ofrecer algo que no va a funcionar.
    if (!token) return null;

    if (isDone) {
        return (
            <section className="help-feedback help-feedback--done">
                <CheckmarkCircle20Filled className="help-feedback__doneIcon" />
                <span>{t('helpDocumentation.feedback.thanks')}</span>
            </section>
        );
    }

    const detail = ComponentFunctions.buildFeedback(undefined, rating, comment);
    const canSendDetail = ComponentFunctions.hasSomethingToSay(detail) && !isSending;
    const remaining = ComponentFunctions.remainingCommentLength(comment);

    return (
        <section className="help-feedback">
            <div className="help-feedback__ask">
                <span className="help-feedback__question">
                    {t('helpDocumentation.feedback.question')}
                </span>

                <div className="help-feedback__answers">
                    <Button
                        size="small"
                        appearance={wasHelpful === true ? 'primary' : 'outline'}
                        icon={wasHelpful === true ? <ThumbLike20Filled /> : <ThumbLike20Regular />}
                        disabled={isSending}
                        onClick={() => onAnswer(true)}
                    >
                        {t('helpDocumentation.feedback.yes')}
                    </Button>
                    <Button
                        size="small"
                        appearance={wasHelpful === false ? 'primary' : 'outline'}
                        icon={
                            wasHelpful === false ? <ThumbDislike20Filled /> : <ThumbDislike20Regular />
                        }
                        disabled={isSending}
                        onClick={() => onAnswer(false)}
                    >
                        {t('helpDocumentation.feedback.no')}
                    </Button>

                    {isSending && wasHelpful !== undefined && <Spinner size="extra-tiny" />}
                </div>
            </div>

            {/* El detalle aparece recién con la respuesta guardada: antes sería un
                formulario delante de una pregunta de un clic. */}
            {wasHelpful !== undefined && !failed && (
                <div className="help-feedback__detail">
                    <span className="help-feedback__detailTitle">
                        {t('helpDocumentation.feedback.more')}
                    </span>

                    <div
                        className="help-feedback__stars"
                        role="radiogroup"
                        aria-label={t('helpDocumentation.feedback.rating')}
                    >
                        {ComponentFunctions.FEEDBACK_RATING_VALUES.map(value => (
                            <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={rating === value}
                                aria-label={t('helpDocumentation.feedback.stars', { count: value })}
                                className={
                                    'help-feedback__star' +
                                    (rating !== undefined && value <= rating ? ' is-on' : '')
                                }
                                // Volver a tocar la estrella elegida la quita: sin esto
                                // un puntaje puesto por error no se puede deshacer.
                                onClick={() => setRating(rating === value ? undefined : value)}
                            >
                                {rating !== undefined && value <= rating ? (
                                    <Star20Filled />
                                ) : (
                                    <Star20Regular />
                                )}
                            </button>
                        ))}
                    </div>

                    <Textarea
                        className="help-feedback__comment"
                        value={comment}
                        resize="vertical"
                        placeholder={t('helpDocumentation.feedback.placeholder')}
                        maxLength={ComponentFunctions.FEEDBACK_COMMENT_MAX_LENGTH}
                        onChange={(_, data) => setComment(data.value)}
                    />

                    <div className="help-feedback__actions">
                        {/* El contador aparece cerca del final, que es cuando importa. */}
                        {remaining <= 200 && (
                            <span className="help-feedback__counter">
                                {t('helpDocumentation.feedback.remaining', { count: remaining })}
                            </span>
                        )}
                        <Button
                            size="small"
                            appearance="primary"
                            disabled={!canSendDetail}
                            onClick={onSendDetail}
                        >
                            {t('helpDocumentation.feedback.send')}
                        </Button>
                    </div>
                </div>
            )}

            {failed && (
                <span className="help-feedback__error" role="alert">
                    {t('helpDocumentation.feedback.error')}
                </span>
            )}
        </section>
    );
};

export default HelpDocumentationFeedbackComponent;
